import { defineMessages } from 'react-intl';

import {
  ASSIGNMENT_ASSESSMENTS,
  ASSIGNMENT_COMMENT_REASON,
  SUBMISSION_STATUSES,
} from '@didaskill/domain/assignments/constants';

export const submissionStatusMessages = defineMessages<SUBMISSION_STATUSES>({
  [SUBMISSION_STATUSES.IN_PROGRESS]: {
    defaultMessage: 'Devoir commencé',
  },
  [SUBMISSION_STATUSES.SUBMITTED]: {
    defaultMessage: 'En attente de correction',
  },
  [SUBMISSION_STATUSES.CORRECTION_IN_PROGRESS]: {
    defaultMessage: 'En cours de correction',
  },
  [SUBMISSION_STATUSES.CORRECTED]: {
    defaultMessage: 'Corrigé',
  },
  [SUBMISSION_STATUSES.COMMENTED]: {
    defaultMessage: 'Nouveau dépôt demandé',
  },
});

/**
 * Messages are duplicated from domain/constants.
 */
export const submissionAssessmentMessages = defineMessages<ASSIGNMENT_ASSESSMENTS>({
  [ASSIGNMENT_ASSESSMENTS.VERY_SATISFACTORY]: {
    defaultMessage: 'Très satisfaisant',
  },
  [ASSIGNMENT_ASSESSMENTS.SATISFACTORY]: {
    defaultMessage: 'Satisfaisant',
  },
  [ASSIGNMENT_ASSESSMENTS.UNSATISFACTORY]: {
    defaultMessage: 'Insatisfaisant',
  },
  [ASSIGNMENT_ASSESSMENTS.VERY_UNSATISFACTORY]: {
    defaultMessage: 'Très insatisfaisant',
  },
});

export const submissionCommentReasonMessages = defineMessages<ASSIGNMENT_COMMENT_REASON>({
  [ASSIGNMENT_COMMENT_REASON.DEPOSIT_ISSUE]: {
    defaultMessage: 'Problème avec le dépôt',
  },
  [ASSIGNMENT_COMMENT_REASON.TECHNICAL_ISSUE]: {
    defaultMessage: 'Problème technique',
  },
  [ASSIGNMENT_COMMENT_REASON.MISUNDERSTANDING_OF_INSTRUCTIONS]: {
    defaultMessage: 'Incompréhension des consignes',
  },
  [ASSIGNMENT_COMMENT_REASON.OTHER_REASON]: {
    defaultMessage: 'Autre',
  },
});

export const submissionCorrectionMessages = defineMessages({
  CORRECTION: {
    defaultMessage: 'Correction',
  },

  GRADE: {
    defaultMessage: 'Note',
  },

  GLOBAL_GRADE: {
    defaultMessage: 'Note globale',
  },

  SUBMISSION_GRADED_DATE: {
    defaultMessage: 'Devoir corrigé le {date, date, long} à {date, time, short}',
  },

  SUBMISSION_COMMENTED_DATE: {
    defaultMessage: 'Nouvelle demande de dépôt le {date, date, long} à {date, time, short}',
  },

  CORRECTION_DATE: {
    defaultMessage: 'Corrigé le {date} - {time}',
  },

  CORRECTOR: {
    defaultMessage: 'Correcteur',
  },

  ASSESSMENT: {
    defaultMessage: 'Appréciation',
  },

  COMMENT: {
    defaultMessage: 'Commentaire',
  },

  ORAL_DATE: {
    defaultMessage: "Date de l'oral blanc",
  },

  UPDATE_CORRECTION: {
    defaultMessage: 'Modifier la correction',
  },

  UPDATE_COMMENT: {
    defaultMessage: 'Modifier le commentaire',
  },

  REASON: {
    defaultMessage: 'Motif',
  },
} as const);

/**
 * Messages are duplicated from domain/assignment-submissions/utils.
 */
export const assignmentAiContextMessages = defineMessages({
  TITLE: {
    id: 'assignment.aiContext.title',
    defaultMessage: 'Titre du devoir : {assignmentTitle}',
  },
  DESCRIPTION: {
    id: 'assignment.aiContext.description',
    defaultMessage: 'Énoncé : {assignmentDescription}',
  },
  DESCRIPTION_NOT_PROVIDED: {
    id: 'assignment.aiContext.descriptionNotProvided',
    defaultMessage: 'Non renseigné',
  },
  GRADING_SCALE: {
    id: 'assignment.aiContext.gradingScale',
    defaultMessage: 'Barème : note sur {gradingScale}',
  },
  STUDENT_COMMENT: {
    id: 'assignment.aiContext.studentComment',
    defaultMessage: "Commentaire de l'étudiant : {comment}",
  },
  STUDENT_DEPOSIT_COMMENT: {
    id: 'assignment.aiContext.studentDepositComment',
    defaultMessage: "Commentaire de l'étudiant sur son dépôt : {comment}",
  },
  STUDENT_COMMENT_NONE: {
    id: 'assignment.aiContext.studentCommentNone',
    defaultMessage: 'Aucun',
  },
  ATTACHMENTS: {
    id: 'assignment.aiContext.attachments',
    defaultMessage: 'Fichiers déposés (noms uniquement, contenu non accessible) : {attachmentList}',
  },
  NO_FILE: {
    id: 'assignment.aiContext.noFile',
    defaultMessage: 'Aucun fichier',
  },
  CORRECTION_SECTION: {
    id: 'assignment.aiContext.correctionSection',
    defaultMessage: '--- Correction du formateur ---',
  },
  GLOBAL_FEEDBACK: {
    id: 'assignment.aiContext.globalFeedback',
    defaultMessage: 'Feedback global : {globalFeedback}',
  },
  DEPOSIT_FEEDBACK: {
    id: 'assignment.aiContext.depositFeedback',
    defaultMessage: 'Feedback sur le dépôt : {feedback}',
  },
  TEACHER_COMMENT: {
    id: 'assignment.aiContext.teacherComment',
    defaultMessage: 'Commentaire du formateur : {comment}',
  },
  GRADE_WITH_SCALE: {
    id: 'assignment.aiContext.gradeWithScale',
    defaultMessage: 'Note : {grade} / {gradingScale}',
  },
  GRADE_WITHOUT_SCALE: {
    id: 'assignment.aiContext.gradeWithoutScale',
    defaultMessage: 'Note : {grade}',
  },
  ASSESSMENT: {
    id: 'assignment.aiContext.assessment',
    defaultMessage: 'Appréciation : {assessment}',
  },
  ASSESSMENT_NOT_PROVIDED: {
    id: 'assignment.aiContext.assessmentNotProvided',
    defaultMessage: 'Non renseignée',
  },
  PRE_SUBMISSION_PROCESS: {
    id: 'assignment.aiContext.preSubmissionProcess',
    defaultMessage:
      "Processus : le formateur fournit en pièce jointe un modèle de devoir (souvent Word) contenant la consigne, le travail demandé et parfois une auto-évaluation ou une grille réservée au correcteur. L'étudiant complète ce modèle puis dépose sa version avant envoi.",
  },
  DESCRIPTION_SUPPLEMENT: {
    id: 'assignment.aiContext.descriptionSupplement',
    defaultMessage: "Complément d'énoncé (texte saisi sur la plateforme) : {description}",
  },
  INSTRUCTOR_MODEL: {
    id: 'assignment.aiContext.instructorModel',
    defaultMessage: 'Modèle / consigne fourni par le formateur : {documentNames}',
  },
  ANALYZED_INSTRUCTION: {
    id: 'assignment.aiContext.analyzedInstruction',
    defaultMessage: 'Modèle de consigne analysé : {documentNames}',
  },
  INSTRUCTION_NOT_ANALYZED: {
    id: 'assignment.aiContext.instructionNotAnalyzed',
    defaultMessage:
      'Modèle de consigne : présent mais non analysé (format ou taille non pris en charge).',
  },
  STUDENT_SUBMISSION_ANALYZED: {
    id: 'assignment.aiContext.studentSubmissionAnalyzed',
    defaultMessage: "Rendu déposé par l'étudiant (analysé) : {documentNames}",
  },
  STUDENT_SUBMISSION_NOT_ANALYZED: {
    id: 'assignment.aiContext.studentSubmissionNotAnalyzed',
    defaultMessage: 'Rendu déposé (non analysé) : {documentNames}',
  },
  SKIPPED_FILES: {
    id: 'assignment.aiContext.skippedFiles',
    defaultMessage: 'Fichiers non analysés : {documentNames}',
  },
  PRE_SUBMISSION_ANALYSIS_INSTRUCTION: {
    id: 'assignment.aiContext.preSubmissionAnalysisInstruction',
    defaultMessage:
      "Consigne d'analyse : compare le rendu étudiant au « Travail demandé » et aux critères de la consigne. Ne pas réévaluer les zones réservées au correcteur (note, appréciation, grille du correcteur), sauf si l'étudiant les a remplies par erreur.",
  },
} as const);
